/* License update request — posts a license key to the Clean Slate licensing
 * backend. If the license is active, the backend emails a time-limited,
 * signed download link (via Resend) to the address on file for that license.
 * The theme ZIP is never exposed to the browser here.
 *
 * Flow:
 *   1. Client-side validate: license key shape (CS-XXXX-XXXX-XXXX-XXXX).
 *   2. POST { license_key } to {api}/request-update.
 *   3. On success, tell the user to check the inbox (masked address from
 *      the response). Translate machine errors into friendly messages.
 *
 * Mirrors license-activate.js. Backend is a different origin — CORS is
 * configured permissively on the worker.
 */
(function () {
  'use strict';

  var KEY_RE = /^CS-[0-9A-HJ-NP-TV-Z]{4}-[0-9A-HJ-NP-TV-Z]{4}-[0-9A-HJ-NP-TV-Z]{4}-[0-9A-HJ-NP-TV-Z]{4}$/;

  function normalizeLicenseKey(raw) {
    var v = (raw || '').trim().toUpperCase().replace(/[\s_-]/g, '');
    if (v.length !== 18 || v.slice(0, 2) !== 'CS') return (raw || '').trim().toUpperCase();
    return v.slice(0, 2) + '-' + v.slice(2, 6) + '-' + v.slice(6, 10) + '-' + v.slice(10, 14) + '-' + v.slice(14, 18);
  }

  function setStatus(el, kind, text) {
    el.className = 'csp-update__status' + (kind ? ' is-' + kind : '');
    el.textContent = text || '';
  }

  function setLoading(form, isLoading) {
    var btn = form.querySelector('.csp-update__submit');
    if (isLoading) {
      btn.disabled = true;
      btn.classList.add('is-loading');
    } else {
      btn.disabled = false;
      btn.classList.remove('is-loading');
    }
  }

  function friendlyError(payload, support) {
    if (!payload || typeof payload !== 'object') {
      return 'Something went wrong. Try again in a moment, or email ' + support + '.';
    }
    if (payload.message) return payload.message;
    switch (payload.error) {
      case 'not_found':       return "We can't find that license key. Double-check it — keys look like CS-XXXX-XXXX-XXXX-XXXX.";
      case 'revoked':         return 'This license has been revoked. Contact ' + support + ' for help.';
      case 'not_activated':   return "This license hasn't been activated yet. Activate it on the activation page first, then request your update.";
      case 'no_email':        return 'We have no email on file for this license. Email ' + support + ' and we’ll send your update manually.';
      case 'invalid_license_key': return 'That license key looks invalid. Check for typos and try again.';
      case 'rate_limited':    return 'Too many requests. Try again in an hour.';
      default:                return 'Something went wrong (' + (payload.error || 'unknown') + '). Email ' + support + '.';
    }
  }

  // One badge line covering every theme — the backend serves all three, and
  // the /version endpoint answers per Shopify product ID.
  var THEME_PRODUCT_IDS = ['8952973263002', '8693549662362', '8725727445146'];

  function showVersionBadge(api, root) {
    var line = root.querySelector('[data-version-line]');
    if (!line || !api) return;
    Promise.all(THEME_PRODUCT_IDS.map(function (id) {
      return fetch(api + '/version?product=' + id, { credentials: 'omit', cache: 'no-store' })
        .then(function (res) { return res.json(); })
        .catch(function () { return null; });
    }))
      .then(function (all) {
        var parts = [];
        all.forEach(function (b) {
          if (!b || !b.ok || !b.version) return;
          // "Clean Slate Signature" → "Signature" keeps the badge compact.
          var name = String(b.product || '').replace(/^Clean Slate\s+/i, '') || 'Theme';
          parts.push(name.replace(/[<>]/g, '') + ' <b>v' + String(b.version).replace(/[<>]/g, '') + '</b>');
        });
        if (!parts.length) return;
        var label = line.getAttribute('data-version-label') || 'Latest version';
        line.innerHTML = label + ': ' + parts.join(' · ');
        line.hidden = false;
      })
      .catch(function () { /* leave the badge hidden on any error */ });
  }

  function attach(form) {
    var api = (form.dataset.api || '').replace(/\/+$/, '');
    var support = form.dataset.support || 'support@buildcleanslate.com';
    var statusEl = form.querySelector('.csp-update__status');
    var keyInput = form.querySelector('input[name="license_key"]');

    showVersionBadge(api, form.closest('.csp-update__inner') || document);

    /* Auto-normalize key on blur so the user SEES the canonical format. */
    keyInput.addEventListener('blur', function () {
      keyInput.value = normalizeLicenseKey(keyInput.value);
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      setStatus(statusEl, null, '');

      var key = normalizeLicenseKey(keyInput.value);
      keyInput.value = key;

      if (!KEY_RE.test(key)) {
        setStatus(statusEl, 'error', 'License key should look like CS-XXXX-XXXX-XXXX-XXXX. Check for typos.');
        keyInput.focus();
        return;
      }
      if (!api) {
        setStatus(statusEl, 'error', 'This page is misconfigured — no backend URL set. Email ' + support + '.');
        return;
      }

      setLoading(form, true);

      fetch(api + '/request-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ license_key: key }),
        credentials: 'omit',
        cache: 'no-store'
      })
      .then(function (res) {
        return res.json().then(function (body) { return { status: res.status, body: body }; });
      })
      .then(function (r) {
        setLoading(form, false);
        if (r.status >= 200 && r.status < 300 && r.body && r.body.ok) {
          var version = r.body.version ? ' (v' + r.body.version + ')' : '';
          var dest = r.body.sent_to ? ' to ' + r.body.sent_to : ' to the address on your license';
          setStatus(statusEl, 'success',
            'Done! We’ve emailed a secure download link' + version + dest +
            '. The link expires in 1 hour — check your inbox (and spam folder).');
          form.querySelector('.csp-update__submit-label').textContent = 'Email sent ✓';
          return;
        }
        setStatus(statusEl, 'error', friendlyError(r.body, support));
      })
      .catch(function () {
        setLoading(form, false);
        setStatus(statusEl, 'error', 'Network error. Check your connection and try again.');
      });
    });
  }

  function init() {
    document.querySelectorAll('.csp-update__form').forEach(attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();